'use client';

import { Dialog, DialogBackdrop, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react';
import { useTranslations } from 'next-intl';
import React, { Fragment } from 'react';

import Button from '@/components/button/Button';
import { ButtonStyle } from '@/components/button/ButtonStyle.enum';

import { withOpacity } from '@cineamo/ui/src/helpers/color-helper';

import { useDomainConfiguration } from '@/providers/DomainConfigurationProvider';

type AppDiscountPopupProps = {
    isOpen: boolean;
    onClose: () => void;
    onContinue: () => void;
};

const APP_DOWNLOAD_URL = 'https://www.cineamo.com/download-app';

const AppDiscountPopup: React.FC<AppDiscountPopupProps> = ({ isOpen, onClose, onContinue }) => {
    const t = useTranslations();
    const domainConfiguration = useDomainConfiguration();
    const style = domainConfiguration.style;

    return (
        <Transition
            show={isOpen}
            as={Fragment}>
            <Dialog
                as="div"
                className="relative z-[60]"
                open={isOpen}
                onClose={onClose}>
                <TransitionChild
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0">
                    <DialogBackdrop className="fixed inset-0 backdrop-blur-sm">
                        <div
                            className="absolute inset-0"
                            style={{
                                backgroundColor: style?.backgroundColor || '#000000',
                                filter: 'brightness(25%)',
                                opacity: 0.3
                            }}
                        />
                    </DialogBackdrop>
                </TransitionChild>

                <div className="fixed inset-0 flex items-center justify-center p-24">
                    <TransitionChild
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0 scale-95"
                        enterTo="opacity-100 scale-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100 scale-100"
                        leaveTo="opacity-0 scale-95">
                        <DialogPanel
                            className="relative max-w-[400px] w-full rounded-8 overflow-hidden"
                            style={{
                                background: withOpacity(style?.backgroundColor, 1.0),
                                color: style?.onSurfaceColor
                            }}>
                            <div
                                className="p-24 md:p-32"
                                style={{
                                    backgroundColor: style?.surfaceColor
                                }}>
                                <DialogTitle className="text-headline-20 text-center">
                                    {t('app-discount-popup.title')}
                                </DialogTitle>
                                <p className="text-body-14 text-center mt-12 opacity-80">
                                    {t('app-discount-popup.text')}
                                </p>
                                <div className="flex flex-col space-y-12 mt-24">
                                    <Button
                                        className="w-full h-48"
                                        css={{
                                            backgroundColor: style?.primaryColor,
                                            color: style?.onPrimaryColor
                                        }}
                                        onClick={() => {
                                            window.open(APP_DOWNLOAD_URL, '_blank');
                                            onClose();
                                        }}>
                                        {t('app-discount-popup.download-app')}
                                    </Button>
                                    <Button
                                        className="w-full h-48"
                                        style={ButtonStyle.text_only}
                                        css={{
                                            color: style?.onSurfaceColor
                                        }}
                                        onClick={onContinue}>
                                        {t('app-discount-popup.continue')}
                                    </Button>
                                </div>
                            </div>
                        </DialogPanel>
                    </TransitionChild>
                </div>
            </Dialog>
        </Transition>
    );
};

export default AppDiscountPopup;
